import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  styled,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import axiosInstance from '../../lib/axios';
import type { LicenseStatus } from '../../types/licenses';

interface GeneratedKeyPair {
  publicKey: string;
  privateKey: string;
  warning: string;
}

const HeaderBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(124, 58, 237, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
}));

const StatusCard = styled(Card)<{ available?: boolean }>(({ available }) => ({
  borderRadius: '16px',
  border: `2px solid ${available ? alpha('#22c55e', 0.3) : alpha('#f59e0b', 0.3)}`,
  backgroundColor: available ? alpha('#22c55e', 0.05) : alpha('#f59e0b', 0.05),
}));

const fetchLicenseStatus = async (): Promise<LicenseStatus> => {
  const response = await axiosInstance.get('/licenses/status');
  return response.data;
};

const generateKeyPair = async (): Promise<GeneratedKeyPair> => {
  const response = await axiosInstance.post('/licenses/generate-keys');
  return response.data;
};

export const LicenseList = () => {
  const navigate = useNavigate();
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [generatedKeys, setGeneratedKeys] = useState<GeneratedKeyPair | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const { data: licenseStatus, isLoading } = useQuery({
    queryKey: ['license-status'],
    queryFn: fetchLicenseStatus,
  });

  const generateKeysMutation = useMutation({
    mutationFn: generateKeyPair,
    onSuccess: (data) => {
      setGeneratedKeys(data);
      setKeyDialogOpen(true);
    },
  });

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCloseDialog = () => {
    setKeyDialogOpen(false);
    // Keep the keys visible in case they want to copy again
  };

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <KeyIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              Desktop Licenses
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            Generate and manage license keys for desktop app activation (Data Sovereignty mode)
          </Typography>
        </Box>
      </HeaderBox>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<VpnKeyIcon />}
          onClick={() => navigate('/licenses/activate')}
          disabled={!licenseStatus?.available}
          sx={{
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            borderColor: '#059669',
            color: '#059669',
            '&:hover': {
              borderColor: '#047857',
              backgroundColor: 'rgba(5, 150, 105, 0.08)',
            },
            '&.Mui-disabled': {
              borderColor: '#ccc',
              color: '#666',
            },
          }}
        >
          Activate License
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/licenses/generate')}
          disabled={!licenseStatus?.available}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.35)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)',
              boxShadow: '0 6px 20px 0 rgba(124, 58, 237, 0.45)',
              transform: 'translateY(-2px)',
            },
            '&.Mui-disabled': {
              background: '#ccc',
              color: '#666',
            },
          }}
        >
          Generate New License
        </Button>
      </Box>

      {!isLoading && licenseStatus && (
        <StatusCard available={licenseStatus.available} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {licenseStatus.available ? (
                <CheckCircleIcon sx={{ fontSize: 40, color: '#22c55e' }} />
              ) : (
                <WarningIcon sx={{ fontSize: 40, color: '#f59e0b' }} />
              )}
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {licenseStatus.available ? 'License Generation Ready' : 'Configuration Required'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {licenseStatus.message}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </StatusCard>
      )}

      <Card sx={{ borderRadius: '16px' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <KeyIcon sx={{ fontSize: 80, color: alpha('#7c3aed', 0.2), mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Desktop License Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
              Generate cryptographically signed license keys for customers who purchase the Data Sovereignty
              desktop application. Each license includes organization details, user/location limits, and expiration date.
            </Typography>

            <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                How it works:
              </Typography>
              <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                <li>Select an organization and configure license parameters</li>
                <li>Generate a signed license key and signature</li>
                <li>Provide both to the customer for desktop app activation</li>
                <li>The desktop app validates the signature offline using the embedded public key</li>
              </Box>
            </Box>

            {licenseStatus?.available && (
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => navigate('/licenses/generate')}
                sx={{
                  mt: 4,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                  borderRadius: '10px',
                  padding: '12px 32px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '16px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)',
                  },
                }}
              >
                Generate Your First License
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {!licenseStatus?.available && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Setup Instructions
          </Typography>
          <Typography variant="body2">
            To enable license generation, you need to configure the RSA private key:
          </Typography>
          <Box component="ol" sx={{ mt: 1, pl: 2 }}>
            <li>Generate a new RSA key pair by clicking the button below</li>
            <li>Save the private key securely (never commit to version control)</li>
            <li>Set the <code>LICENSE_PRIVATE_KEY</code> environment variable on the backend</li>
            <li>Embed the public key in the desktop application</li>
          </Box>
          <Button
            variant="contained"
            startIcon={generateKeysMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <VpnKeyIcon />}
            onClick={() => generateKeysMutation.mutate()}
            disabled={generateKeysMutation.isPending}
            sx={{
              mt: 2,
              background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #047857 0%, #0f766e 100%)',
              },
            }}
          >
            {generateKeysMutation.isPending ? 'Generating...' : 'Generate RSA Key Pair'}
          </Button>
          {generateKeysMutation.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Failed to generate keys. Please try again.
            </Typography>
          )}
        </Alert>
      )}

      {/* Generated Keys Dialog */}
      <Dialog
        open={keyDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon sx={{ color: '#059669' }} />
            <Typography variant="h6" fontWeight={600}>
              RSA Key Pair Generated
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {generatedKeys && (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600}>
                  Security Warning
                </Typography>
                <Typography variant="body2">
                  {generatedKeys.warning}
                </Typography>
              </Alert>

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Public Key (embed in desktop app)
              </Typography>
              <Box sx={{ position: 'relative', mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={generatedKeys.publicKey}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '12px' }
                  }}
                />
                <Tooltip title={copySuccess === 'public' ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    onClick={() => handleCopy(generatedKeys.publicKey, 'public')}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: copySuccess === 'public' ? '#22c55e' : 'rgba(0,0,0,0.04)',
                      color: copySuccess === 'public' ? '#fff' : 'inherit',
                      '&:hover': {
                        backgroundColor: copySuccess === 'public' ? '#22c55e' : 'rgba(0,0,0,0.08)',
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Private Key (set as LICENSE_PRIVATE_KEY env var)
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={generatedKeys.privateKey}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '12px' }
                  }}
                />
                <Tooltip title={copySuccess === 'private' ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    onClick={() => handleCopy(generatedKeys.privateKey, 'private')}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: copySuccess === 'private' ? '#22c55e' : 'rgba(0,0,0,0.04)',
                      color: copySuccess === 'private' ? '#fff' : 'inherit',
                      '&:hover': {
                        backgroundColor: copySuccess === 'private' ? '#22c55e' : 'rgba(0,0,0,0.08)',
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: '8px' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
